import React, { useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import { Box } from '@mui/material';
import ListHeader from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import ITable, { AvailableColumn } from '../uikit/itable/ITable';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { useGetAllContacts, ContactAvailableColumns, ContactSortParam } from '../services/react-query/contacts';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { Contact } from '../types/Contact';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import { ContactColumns, ContactFilters } from '../utility/contact';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { useIsMobile } from '../hooks/useIsMobile';
import PreferencePill from '../components/preferences/PreferencePill';
import { RolePillsCell } from '../components/preferences/RolePillsCell';
import _ from 'lodash';
import { FilterState } from '../components/common/filters/interfaces';
import { useDownloadCsv } from '../hooks/useDownloadCsv';

const ContactsList: React.FC = () => {
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { userSorts } = useUserSorts<ContactSortParam>(UserConfigurableModule.CONTACTS);

  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const { data: preferenceData } = useGetAllPreferences({
    params: {
      filters: { keys: [PREFERENCES_VALID_KEYS.CONTACT_ROLE, PREFERENCES_VALID_KEYS.CONTACT_TITLE] },
    },
  });

  const prefByUuid = useMemo(() => _.keyBy(preferenceData ?? [], 'uuid'), [preferenceData]);

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState,
    sort: userSorts,
  };

  const { data, isLoading: loading } = useGetAllContacts({
    params: {
      ...baseParams,
      page: currentPage,
    },
    enabled: true,
  });

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: 'contacts',
  });

  if (loading) return <Loading />;

  const contacts = data?.data || [];
  const meta = data?.meta;

  const pillColumns: AvailableColumn<Contact, ContactAvailableColumns>[] = ContactColumns.map((col) => {
    if (col.key === 'contact_title_value') {
      return {
        ...col,
        render: (row: Contact) => {
          return <PreferencePill {...prefByUuid[row.contact_title_uuid ?? '']} />;
        },
      };
    }

    if (col.key === 'roles') {
      return {
        ...col,
        sortable: false,
        render: (row: Contact) => {
          const rolesUuids = _.isArray(row.roles_uuids) ? row.roles_uuids : [];
          return <RolePillsCell rolesUuids={rolesUuids} prefByUuid={prefByUuid} />;
        },
      };
    }

    return col;
  });

  return (
    <Container maxWidth="xl">
      <ListHeader
        title="Contacts"
        module={UserConfigurableModule.CONTACTS}
        moduleFilters={ContactFilters(preferenceData)}
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={ContactFilters(preferenceData)}
            module={UserConfigurableModule.CONTACTS}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={listPageTableWrapperStyles}>
          <ITable<Contact, ContactAvailableColumns>
            availableColumns={pillColumns}
            data={contacts}
            module={UserConfigurableModule.CONTACTS}
            page={currentPage}
            count={meta?.total_pages ?? 1}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default ContactsList;
